import React, { useCallback, useEffect, useRef, useState } from 'react';
import SearchBar from './SearchBar.js';
import FilterBar from './FilterBar.js';
import ResultsTable from './ResultsTable.js';
import EmptyState from './EmptyState.js';
import TooManyResultsBanner from './TooManyResultsBanner.js';
import Pagination from './Pagination.js';
import ScrollToTop from './ScrollToTop.js';
import LoadingSkeleton from './LoadingSkeleton.js';
import { flatTableColCount } from './ResultsTable.js';
import type { SyntaxNode } from './ResultRow.js';
import { writeLastSearchCount, type CountSource } from './searchCounts.js';
import ScopePanel from './ScopePanel.js';
import {
  applyFacetSelection,
  buildNarrowedSearchUrl,
  classifyToolFailure,
  type RefusalFailure,
} from './toolError.js';

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  'https://api.repolens.awsprinl1np.click';
const SEARCH_SYMBOLS_ENDPOINT = `${API_BASE}/api/search/symbols`;
// S-DM-001: the branch facet's real source — persisted repository data.
const BRANCHES_ENDPOINT = `${API_BASE}/api/search/branches`;

const PAGE_SIZE = 20;
const TOO_MANY_THRESHOLD = 100;

// S-DM-005: `refused` is a first-class state, DISTINCT from both `empty` (the
// corpus really has no match) and `error` (RepoLens is broken).
type SearchState =
  | 'idle'
  | 'loading'
  | 'results'
  | 'empty'
  | 'too-many'
  | 'refused'
  | 'error'
  | 'session-expired';

type SessionKind = 'pending' | 'signed-in' | 'signed-out';

function getUrlParam(name: string): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get(name) ?? '';
}

// S-SEARCH401-1: pure classification of a resolved /api/search/symbols response, kept
// separate from performSearch so 401-vs-other-status logic is unit-testable
// without mocking fetch end-to-end. Only called for non-throwing fetches —
// a thrown fetch (network failure) is classified inline in performSearch's
// catch, since there is no Response to inspect.
export type SearchFailureClassification =
  | { kind: 'session-expired' }
  | { kind: 'server-error' };

export function classifySearchFailure(res: Response): SearchFailureClassification {
  return res.status === 401 ? { kind: 'session-expired' } : { kind: 'server-error' };
}

// S-SEARCH401-1: pure guard condition for the on-mount ?q= auto-search,
// extracted so AC4 is unit-testable without React Testing Library (frontend
// has no RTL installed per QA test-strategy.md recommendation).
export function shouldAutoSearchOnMount(sessionKind: SessionKind, alreadyMounted: boolean): boolean {
  return sessionKind === 'signed-in' && !alreadyMounted;
}

// S-DM-006: resolve the count the UI reasons about.
//
// The backend does not emit `totalCount` yet — that lands in MS-DM-4 — so this
// ships the frontend half FIRST: prefer `totalCount` when present, and retain
// the `nodes.length` fallback for every response that omits it. Both branches
// are live and both are unit-tested; the fallback is deliberately kept.
//
// `totalCount: 0` is a real zero, not "absent": presence is decided by a type
// check, never by `??`/falsiness, which would misread 0 as missing.
export interface ResolvedResultCount {
  /** The number to show the user. */
  total: number;
  /** Which branch produced it — recorded in the localStorage envelope. */
  source: CountSource;
  /** True when `total` exceeds the rows on this page, so the page is partial. */
  isPartialPage: boolean;
}

export function resolveResultCount(
  totalCount: unknown,
  nodesLength: number,
): ResolvedResultCount {
  const hasTotalCount =
    typeof totalCount === 'number' && Number.isFinite(totalCount) && totalCount >= 0;

  if (!hasTotalCount) {
    // Fallback path — unchanged behaviour (AC2). Also covers a non-numeric or
    // negative `totalCount` from a misbehaving backend.
    return { total: nodesLength, source: 'nodesLength', isPartialPage: false };
  }

  // Clamp up to the rows actually rendered: `totalCount: 0` alongside a
  // non-empty page must never be presented as "0 results" while rows are on
  // screen (AC4).
  const total = Math.max(Math.floor(totalCount), nodesLength);
  return { total, source: 'totalCount', isPartialPage: total > nodesLength };
}

// S-DM-006 AC1: the threshold is evaluated against the resolved total, not
// against `nodes.length`. An empty page is never "too many" regardless of total.
export function isTooManyResults(total: number, nodesLength: number): boolean {
  return nodesLength > 0 && total >= TOO_MANY_THRESHOLD;
}

// S-DM-001: the branch control's state, derived from PERSISTED repository data
// fetched from GET /api/search/branches — never from the current page of search
// results, which would make the facet a page-size artifact (the bug class
// S-DM-006 just fixed for counts).
//
// Branch is not a corpus-wide dimension: two repositories' `main` branches are
// unrelated, so with no repository selected the control is DISABLED with a
// visible reason rather than silently empty.
export interface BranchControlState {
  disabled: boolean;
  /** Visible reason when disabled; empty string when enabled. */
  reason: string;
}

export function resolveBranchControlState(
  selectedRepo: string,
  branches: string[],
  loading: boolean,
): BranchControlState {
  if (!selectedRepo.trim()) {
    return {
      disabled: true,
      reason: 'Select a repository first — branch is not a corpus-wide filter',
    };
  }
  if (loading) return { disabled: true, reason: 'Loading branches…' };
  if (branches.length === 0) {
    return { disabled: true, reason: `No indexed branches for ${selectedRepo.trim()}` };
  }
  return { disabled: false, reason: '' };
}

// S-DM-005: the whole state transition for a 2xx /tools/call body, as a pure
// function, so AC1 is asserted directly instead of inferred from the DOM.
//
// The refusal branch is evaluated FIRST and returns before the
// `result.data ?? []` / `nodes.length === 0` fall-through is ever reached — that
// fall-through is what used to render a refusal as "no results found".
export type SearchOutcome =
  | { state: 'refused'; failure: RefusalFailure }
  | { state: 'error'; message: string; detail: string }
  | {
      state: 'results' | 'empty' | 'too-many';
      nodes: SyntaxNode[];
      count: ResolvedResultCount;
    };

export function resolveSearchOutcome(body: unknown): SearchOutcome {
  const result = (body ?? {}) as {
    success?: unknown;
    data?: SyntaxNode[] | null;
    error?: unknown;
    totalCount?: unknown;
  };

  // A refusal is signalled by success === false. Classification branches on the
  // error CODE only — never on the message (see toolError.ts).
  if (result.success === false) {
    const failure = classifyToolFailure(result.error);
    return failure.kind === 'refusal'
      ? { state: 'refused', failure }
      : { state: 'error', message: failure.message, detail: failure.detail };
  }

  const nodes: SyntaxNode[] = result.data ?? [];
  const count = resolveResultCount(result.totalCount, nodes.length);

  if (nodes.length === 0) return { state: 'empty', nodes, count };
  if (isTooManyResults(count.total, nodes.length)) return { state: 'too-many', nodes, count };
  return { state: 'results', nodes, count };
}

/** S-DM-001: parse GET /api/search/branches into a branch list, defensively. */
export function parseBranchesResponse(body: unknown): string[] {
  const branches = (body as { branches?: unknown } | null)?.branches;
  if (!Array.isArray(branches)) return [];
  return branches.filter((b): b is string => typeof b === 'string' && b.trim() !== '');
}

interface SearchPageProps {
  sessionKind: SessionKind;
}

export default function SearchPage({ sessionKind }: SearchPageProps): React.ReactElement {
  // State — URL param-backed (frozen contract: ?q=, ?repo=, ?branch=)
  const [query, setQuery] = useState<string>(() => getUrlParam('q'));
  const [selectedRepo, setSelectedRepo] = useState<string>(() => getUrlParam('repo'));
  const [selectedBranch, setSelectedBranch] = useState<string>(() => getUrlParam('branch'));

  // In-memory state
  const [filterBarExpanded, setFilterBarExpanded] = useState<boolean>(false);
  const [results, setResults] = useState<SyntaxNode[]>([]);
  // S-DM-006: the resolved match count for the current results (totalCount when
  // the response carried one, nodes.length otherwise).
  const [resultCount, setResultCount] = useState<ResolvedResultCount>({
    total: 0,
    source: 'nodesLength',
    isPartialPage: false,
  });
  const [searchState, setSearchState] = useState<SearchState>('idle');
  // S-DM-005: the refusal backing the scope panel; null in every other state.
  const [refusal, setRefusal] = useState<RefusalFailure | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Share link transient state
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  // localStorage state — lazy init (Hot #111)
  const [queryHistory, setQueryHistory] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('repolens:queryHistory') ?? '[]') as string[];
    } catch {
      return [];
    }
  });

  // Derived: active filter count
  const activeFilterCount = (selectedRepo ? 1 : 0) + (selectedBranch ? 1 : 0);

  // Derived: available repos from results
  const availableRepos = Array.from(new Set(results.map((r) => r.repositoryId)));

  // S-DM-001: available branches come from persisted repository data via
  // GET /api/search/branches — NOT from `results`.
  const [availableBranches, setAvailableBranches] = useState<string[]>([]);
  const [branchesLoading, setBranchesLoading] = useState<boolean>(false);
  const branchControl = resolveBranchControlState(selectedRepo, availableBranches, branchesLoading);

  // --- performSearch ---
  // NOTE: useEffect must come AFTER all derived consts it references (Hot #115)
  const performSearch = useCallback(
    async (q: string, repo: string, branch: string): Promise<void> => {
      setSearchState('loading');
      const body: Record<string, string | number> = { limit: 150 };
      if (repo.trim()) body.repository = repo.trim();
      if (q.trim()) body.symbolName = q.trim();
      if (branch.trim()) body.branch = branch.trim();

      let res: Response;
      try {
        res = await fetch(SEARCH_SYMBOLS_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'User-Agent': 'RepoLens-UI/1.0',
          },
          credentials: 'include',
          body: JSON.stringify(body),
        });
      } catch {
        // fetch() itself threw — genuine network failure, no response received (AC2).
        setSearchState('error');
        setErrorMessage('Search failed — check connection');
        return;
      }

      if (!res.ok) {
        // Check status BEFORE parsing JSON (AC1) — the ALB/Core returns 401
        // with an empty body, and res.json() on an empty body throws, which
        // used to be swallowed into a misleading "check connection" message.
        const classification = classifySearchFailure(res);
        if (classification.kind === 'session-expired') {
          setSearchState('session-expired');
          setErrorMessage('Your session has expired — please sign in again');
        } else {
          // Non-401 non-2xx (e.g. 500) — surfaced distinctly, not silently
          // absorbed into a blanket catch (AC3).
          setSearchState('error');
          setErrorMessage('Search failed — check connection');
        }
        return;
      }

      try {
        const outcome = resolveSearchOutcome(await res.json());

        // S-DM-005: a refusal gets its own state and its own panel. It never
        // falls through to the empty-results path, and it never clears into a
        // generic error.
        if (outcome.state === 'refused') {
          setRefusal(outcome.failure);
          setResults([]);
          setSearchState('refused');
          setCurrentPage(1);
          return;
        }
        setRefusal(null);

        if (outcome.state === 'error') {
          // INTERNAL_ERROR: a platform bug. The user gets generic copy — no
          // narrowing controls, because no argument they change would help —
          // and the server's exception prose goes to the console for diagnosis.
          if (outcome.detail) console.warn('[RepoLens] tool INTERNAL_ERROR:', outcome.detail);
          setSearchState('error');
          setErrorMessage(outcome.message);
          setResults([]);
          return;
        }

        const nodes = outcome.nodes;
        // S-DM-006: prefer totalCount, fall back to nodes.length.
        const resolved = outcome.count;

        setSearchState(outcome.state);
        if (outcome.state === 'too-many') setFilterBarExpanded(true);
        setResultCount(resolved);
        setResults(nodes);
        setCurrentPage(1);

        // Update query history — deduplicate with Set<string> (Hot #112), max 5
        if (q.trim()) {
          setQueryHistory((prev) => {
            const next = [q.trim(), ...prev.filter((h) => h !== q.trim())].slice(0, 5);
            try {
              localStorage.setItem('repolens:queryHistory', JSON.stringify(next));
            } catch {}
            return next;
          });
        }

        // S-DM-007: write the versioned envelope through the shared helper,
        // recording per value whether it came from totalCount or the fallback.
        if (repo.trim()) {
          writeLastSearchCount(repo.trim(), resolved.total, resolved.source);
        }
      } catch {
        setSearchState('error');
        setErrorMessage('Search failed — check connection');
      }
    },
    [],
  );

  // On mount (or once session becomes signed-in): auto-search if ?q= is present.
  // Guard on sessionKind === 'signed-in' to avoid unauthenticated fetches that
  // race with the ALB OIDC flow and can overwrite AWSELBAuthNonce cookies.
  // (Hot #115 — after performSearch; nonce-race fix for /search?q=<term> deep links)
  const didMountSearch = useRef(false);
  useEffect(() => {
    if (!shouldAutoSearchOnMount(sessionKind, didMountSearch.current)) return;
    didMountSearch.current = true;
    const initialQ = getUrlParam('q');
    const initialRepo = getUrlParam('repo');
    const initialBranch = getUrlParam('branch');
    // Only auto-search when ?q= is present — ?repo= alone just pre-populates, no search
    if (initialQ.trim()) {
      void performSearch(initialQ, initialRepo, initialBranch);
    }
  }, [performSearch, sessionKind]);

  // S-DM-001: load the branch list for the selected repository from persisted
  // repository data. Re-runs when the repository changes; clears when the
  // repository is deselected (branch is not a corpus-wide dimension).
  // (Hot #115 — placed after the derived consts it depends on.)
  useEffect(() => {
    const repo = selectedRepo.trim();
    if (!repo || sessionKind !== 'signed-in') {
      setAvailableBranches([]);
      setBranchesLoading(false);
      return;
    }

    let cancelled = false;
    setBranchesLoading(true);
    void (async () => {
      try {
        const res = await fetch(`${BRANCHES_ENDPOINT}?repository=${encodeURIComponent(repo)}`, {
          headers: { 'User-Agent': 'RepoLens-UI/1.0' },
          credentials: 'include',
        });
        const parsed = res.ok ? parseBranchesResponse(await res.json()) : [];
        if (!cancelled) setAvailableBranches(parsed);
      } catch {
        // A failed branch lookup leaves the control disabled with a reason,
        // never silently empty-but-enabled.
        if (!cancelled) setAvailableBranches([]);
      } finally {
        if (!cancelled) setBranchesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedRepo, sessionKind]);

  function handleSearch(q: string): void {
    setQuery(q);
    // Update URL params
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (selectedRepo.trim()) params.set('repo', selectedRepo.trim());
    if (selectedBranch.trim()) params.set('branch', selectedBranch.trim());
    window.history.pushState(null, '', `/search?${params.toString()}`);
    void performSearch(q, selectedRepo, selectedBranch);
  }

  function handleClear(): void {
    setQuery('');
    setResults([]);
    setRefusal(null);
    setSearchState('idle');
    setFilterBarExpanded(false);
    window.history.pushState(null, '', '/search');
  }

  function handleFilterApply(repo: string, branch: string): void {
    setSelectedRepo(repo);
    setSelectedBranch(branch);
    // Re-run search with new filters if we have a query
    if (query.trim()) {
      const params = new URLSearchParams();
      if (query.trim()) params.set('q', query.trim());
      if (repo.trim()) params.set('repo', repo.trim());
      if (branch.trim()) params.set('branch', branch.trim());
      window.history.pushState(null, '', `/search?${params.toString()}`);
      void performSearch(query, repo, branch);
    }
  }

  function handleFilterClear(): void {
    setSelectedRepo('');
    setSelectedBranch('');
    if (query.trim()) {
      window.history.pushState(null, '', `/search?q=${encodeURIComponent(query.trim())}`);
      void performSearch(query, '', '');
    }
  }

  // S-DM-005 AC3/AC6: clicking a facet re-issues the SAME query narrowed on
  // THAT dimension — a `repository` facet sets the repository, a `branch` facet
  // sets the branch — updating the URL per the frozen shareable contract
  // (?q=/?repo=/?branch=) and carrying no cursor. The dimension→scope mapping
  // lives in `applyFacetSelection` so it is unit-testable and so a dimension
  // this build cannot narrow on declines instead of re-running the identical
  // search (EXP-DM-5).
  function handleFacetSelect(dimension: string, value: string): void {
    const next = applyFacetSelection(dimension, value, {
      repo: selectedRepo,
      branch: selectedBranch,
    });
    if (!next) return;
    setSelectedRepo(next.repo);
    setSelectedBranch(next.branch);
    window.history.pushState(null, '', buildNarrowedSearchUrl(query, next.repo, next.branch));
    void performSearch(query, next.repo, next.branch);
  }

  function handleRecentSearchClick(q: string): void {
    setQuery(q);
    handleSearch(q);
  }

  function handleCopyShareLink(): void {
    navigator.clipboard.writeText(window.location.href).catch(() => {});
    setShareLinkCopied(true);
    setTimeout(() => setShareLinkCopied(false), 1500);
  }

  const isIdle = searchState === 'idle';
  const isLoading = searchState === 'loading';
  const hasResults = searchState === 'results' || searchState === 'too-many';
  const isEmpty = searchState === 'empty';
  // S-DM-005 AC5: `refused` is in neither hasResults nor isTooMany, so
  // ResultsTable is not rendered at all and no bare TABLE_COLUMNS header row is
  // produced.
  const isRefused = searchState === 'refused' && refusal !== null;
  const isTooMany = searchState === 'too-many';
  const isError = searchState === 'error';
  const isSessionExpired = searchState === 'session-expired';

  return (
    <main className={`search-page${isIdle ? ' search-page--idle' : ''}`}>
      {/* SearchBar — always rendered */}
      <SearchBar
        query={query}
        onQueryChange={setQuery}
        onSearch={handleSearch}
        onClear={handleClear}
        activeFilterCount={activeFilterCount}
        filterBarExpanded={filterBarExpanded}
        onFilterToggle={() => setFilterBarExpanded((v) => !v)}
      />

      {/* Idle: recent searches below input */}
      {isIdle && queryHistory.length > 0 && (
        <div className="recent-searches">
          <div className="recent-searches-label">Recent searches</div>
          {queryHistory.map((q, i) => (
            <button
              key={i}
              type="button"
              className="recent-search-item"
              onClick={() => handleRecentSearchClick(q)}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {isIdle && (
        <p className="search-hint">Type a symbol name and press Enter to search</p>
      )}

      {/* Non-idle layouts */}
      {!isIdle && (
        <>
          {/* FilterBar */}
          <FilterBar
            expanded={filterBarExpanded}
            availableRepos={availableRepos}
            availableBranches={availableBranches}
            branchDisabled={branchControl.disabled}
            branchDisabledReason={branchControl.reason}
            selectedRepo={selectedRepo}
            selectedBranch={selectedBranch}
            onApply={handleFilterApply}
            onClear={handleFilterClear}
          />

          {/* Results summary + share link */}
          {(hasResults || isTooMany) && (
            <div className="results-summary">
              {/* S-DM-006 AC3: when the total exceeds the rows on this page the
                  two numbers are shown as distinct things — the page is
                  disclosed as partial rather than the total being passed off as
                  the row count. */}
              <span data-testid="results-count">
                {resultCount.total} result{resultCount.total !== 1 ? 's' : ''}
                {selectedRepo ? ` in ${selectedRepo}` : ''}
                {resultCount.isPartialPage ? ` — showing ${results.length} on this page` : ''}
              </span>
              <button
                type="button"
                data-testid="share-link-btn"
                className={`share-link-btn${shareLinkCopied ? ' share-link-btn--copied' : ''}`}
                aria-label="Copy share link for this search"
                onClick={handleCopyShareLink}
              >
                {shareLinkCopied ? 'Copied!' : '⎘ Share'}
              </button>
            </div>
          )}

          {/* Too-many banner */}
          {isTooMany && (
            <TooManyResultsBanner
              total={resultCount.total}
              isPartialPage={resultCount.isPartialPage}
            />
          )}

          {/* Loading skeleton */}
          {isLoading && (
            <div className="results-table-wrap">
              <table className="results-table" data-testid="results-table">
                <thead>
                  <tr>
                    <th scope="col">Symbol</th>
                    <th scope="col">Type</th>
                    <th scope="col">File Path</th>
                    <th scope="col">Repository</th>
                  </tr>
                </thead>
                <tbody>
                  <LoadingSkeleton colSpan={flatTableColCount} rows={8} />
                </tbody>
              </table>
            </div>
          )}

          {/* Results table */}
          {(hasResults || isTooMany) && !isLoading && (
            <ResultsTable
              results={results}
              query={query}
              currentPage={currentPage}
              pageSize={PAGE_SIZE}
            />
          )}

          {/* Empty state */}
          {isEmpty && (
            <EmptyState
              query={query}
              hasActiveFilters={activeFilterCount > 0}
              onClearFilters={handleFilterClear}
            />
          )}

          {/* Scope panel (S-DM-005) — a refusal, rendered as a next action.
              Semantically and visually distinct from the error state below:
              this one hands the user a way to narrow, that one does not. */}
          {isRefused && refusal !== null && (
            <ScopePanel failure={refusal} onFacetSelect={handleFacetSelect} />
          )}

          {/* Error state — data-testid="search-error" added for Playwright (DEF-EXPQ-10) */}
          {isError && (
            <div className="empty-state" role="alert" data-testid="search-error">
              <div className="empty-state-title">{errorMessage}</div>
            </div>
          )}

          {/* Session-expired state (S-SEARCH401-1) — distinct from generic error */}
          {isSessionExpired && (
            <div className="empty-state" role="alert" data-testid="search-session-expired">
              <div className="empty-state-title">{errorMessage}</div>
              <a href={`${API_BASE}/auth/sign-in`} className="signin-button" data-testid="search-signin-btn" role="button">
                Sign in
              </a>
            </div>
          )}

          {/* Pagination */}
          {(hasResults || isTooMany) && !isLoading && (
            <Pagination
              currentPage={currentPage}
              pageSize={PAGE_SIZE}
              totalResults={results.length}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}

      <ScrollToTop />
    </main>
  );
}
